import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Flame, Lock, ShieldCheck, CalendarCheck } from 'lucide-react';

interface LandingFooterProps {
  onOpenOrderApp: () => void;
  onOpenStaffPortal: () => void;
  onOpenLegalModal: (type: 'allergen' | 'terms' | 'privacy') => void;
}

export const LandingFooter: React.FC<LandingFooterProps> = ({
  onOpenOrderApp,
  onOpenStaffPortal,
  onOpenLegalModal
}) => {
  const { lang } = useLanguage();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="os-footer">
      <div className="os-container">
        <div className="os-footer-top">
          {/* Brand Info & Contact */}
          <div>
            <div className="os-brand-logo" style={{ marginBottom: '14px' }}>
              <div className="os-brand-icon" style={{ width: '32px', height: '32px' }}>
                <Flame size={18} color="#e05a36" style={{ fill: '#e05a36' }} />
              </div>
              <span className="os-brand-name" style={{ fontSize: '1.4rem' }}>The Oven Story</span>
            </div>

            <p style={{ color: 'var(--os-text-muted)', fontSize: '0.88rem', lineHeight: '1.6', margin: '0 0 14px 0', maxWidth: '360px' }}>
              Aleksanterinkatu 18, 00170 Helsinki, Finland<br />
              Y-tunnus: FI3149821-4 • Puh: +358 40 555 8920<br />
              Email: asiakaspalvelu@theovenstory.fi
            </p>

            <button
              onClick={onOpenOrderApp}
              className="os-btn-book-table"
              style={{ padding: '9px 18px', fontSize: '0.84rem' }}
            >
              <CalendarCheck size={14} />
              <span>{lang === 'fi' ? 'Varaa Pöytä / Tilaa' : 'Book Your Table'}</span>
            </button>
          </div>

          {/* Nav: Menu */}
          <div>
            <h4 style={{ fontSize: '0.78rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--os-amber)', marginBottom: '16px', fontWeight: 700 }}>
              {lang === 'fi' ? 'Ruoka & Baari' : 'Food & Bar'}
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <li>
                <button onClick={() => scrollTo('food')} className="os-nav-link" style={{ padding: 0 }}>
                  {lang === 'fi' ? 'Puu-uunipizzat' : 'Wood-Fired Pizzas'}
                </button>
              </li>
              <li>
                <button onClick={() => scrollTo('bar')} className="os-nav-link" style={{ padding: 0 }}>
                  {lang === 'fi' ? 'Käsityöcocktailit' : 'Craft Cocktails'}
                </button>
              </li>
              <li>
                <button onClick={() => scrollTo('bar')} className="os-nav-link" style={{ padding: 0 }}>
                  {lang === 'fi' ? 'Pienpanimo-oluet & Lonkero' : 'Craft Beers & Lonkero'}
                </button>
              </li>
            </ul>
          </div>

          {/* Nav: Bookings */}
          <div>
            <h4 style={{ fontSize: '0.78rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--os-amber)', marginBottom: '16px', fontWeight: 700 }}>
              {lang === 'fi' ? 'Tilaisuudet' : 'Bookings'}
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <li>
                <button onClick={() => scrollTo('private-booking')} className="os-nav-link" style={{ padding: 0 }}>
                  {lang === 'fi' ? 'Yksityistilaisuudet' : 'Private Bookings'}
                </button>
              </li>
              <li>
                <button onClick={() => scrollTo('private-booking')} className="os-nav-link" style={{ padding: 0 }}>
                  {lang === 'fi' ? 'Yritystapahtumat' : 'Corporate Events'}
                </button>
              </li>
              <li>
                <button onClick={onOpenOrderApp} className="os-nav-link" style={{ padding: 0, color: 'var(--os-copper)' }}>
                  {lang === 'fi' ? 'Pöytätilausjärjestelmä' : 'Table Booking Portal'}
                </button>
              </li>
            </ul>
          </div>

          {/* Legal & Safety (Oiva) */}
          <div>
            <h4 style={{ fontSize: '0.78rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--os-amber)', marginBottom: '16px', fontWeight: 700 }}>
              {lang === 'fi' ? 'Turvallisuus & Ehdot' : 'Safety & Terms'}
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <li>
                <button onClick={() => onOpenLegalModal('allergen')} className="os-nav-link" style={{ padding: 0, color: '#10b981' }}>
                  {lang === 'fi' ? 'Allergeenit & Oiva' : 'Allergens & Oiva'}
                </button>
              </li>
              <li>
                <button onClick={() => onOpenLegalModal('privacy')} className="os-nav-link" style={{ padding: 0 }}>
                  {lang === 'fi' ? 'Tietosuoja (GDPR)' : 'Privacy Policy'}
                </button>
              </li>
              <li>
                <button onClick={() => onOpenLegalModal('terms')} className="os-nav-link" style={{ padding: 0 }}>
                  {lang === 'fi' ? 'Varausehdot' : 'Terms of Service'}
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="os-footer-bottom">
          <p style={{ margin: 0 }}>
            © {new Date().getFullYear()} The Oven Story (theovenstory.fi). All rights reserved. Aleksanterinkatu 18, 00170 Helsinki.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldCheck size={15} color="#10b981" />
            <span>Finnish Food Authority (Oiva) & GDPR ALV Compliant</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
